import { getApiBaseUrl } from './api'

const API_BASE_URL = getApiBaseUrl()
const PROJECT_KEY = 'icc'

export interface User {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
}

interface LoginResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
  tokenType: string
  user?: { id: string; email: string; roles: string[] }
}

class AuthClient {
  private baseUrl = API_BASE_URL
  private projectKey = PROJECT_KEY

  // ── Email/Password Auth ──

  async login(email: string, password: string): Promise<LoginResponse> {
    const res = await fetch(`${this.baseUrl}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Project-Key': this.projectKey },
      body: JSON.stringify({ email, password }),
    })
    const data = await res.json()
    if (!data.success) throw new Error(data.message || 'Login failed')
    const response: LoginResponse = {
      accessToken: data.data.accessToken,
      refreshToken: data.data.refreshToken || '',
      expiresIn: data.data.expiresIn || 3600,
      tokenType: data.data.tokenType || 'Bearer',
      user: data.data.user,
    }
    this.storeTokens(response)
    return response
  }

  async register(email: string, password: string): Promise<{ requiresVerification: boolean; email: string }> {
    const res = await fetch(`${this.baseUrl}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Project-Key': this.projectKey },
      body: JSON.stringify({ email, password }),
    })
    const data = await res.json()
    if (!data.success) throw new Error(data.message || 'Registration failed')
    return { requiresVerification: true, email: data.data.email || email }
  }

  async verifyOtpAndLogin(email: string, otp: string): Promise<LoginResponse> {
    const res = await fetch(`${this.baseUrl}/api/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Project-Key': this.projectKey },
      body: JSON.stringify({ email, otp }),
    })
    const data = await res.json()
    if (!data.success) throw new Error(data.message || 'Verification failed')
    const response: LoginResponse = {
      accessToken: data.data.accessToken,
      refreshToken: data.data.refreshToken || '',
      expiresIn: data.data.expiresIn || 3600,
      tokenType: data.data.tokenType || 'Bearer',
      user: data.data.user,
    }
    this.storeTokens(response)
    return response
  }

  async forgotPassword(email: string): Promise<void> {
    // /forgot-password just generates a UUID reset token and never emails.
    // /send-otp with purpose=password_reset actually delivers the 8-digit OTP.
    const res = await fetch(`${this.baseUrl}/api/auth/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Project-Key': this.projectKey },
      body: JSON.stringify({ email, purpose: 'password_reset' }),
    })
    const data = await res.json()
    if (!data.success) throw new Error(data.message || 'Failed to send reset email')
  }

  async resetPasswordWithOtp(email: string, otp: string, newPassword: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/auth/reset-password-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Project-Key': this.projectKey },
      body: JSON.stringify({ email, otp, newPassword }),
    })
    const data = await res.json()
    if (!data.success) throw new Error(data.message || 'Password reset failed')
  }

  // ── Google OAuth ──

  async getGoogleOAuthUrl(returnPath?: string): Promise<string> {
    // Encode return path as URL-safe base64 into the PATH segment rather
    // than a query param — the backend's URI builder mangles query params
    // it doesn't expect, but preserves the path cleanly.
    let redirectUrl = `${window.location.origin}/callback`
    if (returnPath) {
      const rp = btoa(returnPath).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
      redirectUrl = `${window.location.origin}/callback/r/${rp}`
    }
    const res = await fetch(
      `${this.baseUrl}/api/auth/google/authorize?redirectUrl=${encodeURIComponent(redirectUrl)}&prompt=consent`,
      { method: 'GET', headers: { 'X-Project-Key': this.projectKey } }
    )
    const data = await res.json()
    if (!data.success) throw new Error(data.message || 'Failed to get Google auth URL')
    return data.data.authorizationUrl
  }

  async handleOAuthCallback(): Promise<LoginResponse> {
    let search = window.location.search
    if (search.includes('??')) search = '?' + search.replace(/\?\?+/g, '&').replace(/^\?/, '')
    const params = new URLSearchParams(search)

    const error = params.get('error')
    if (error) throw new Error(params.get('error_description') || error)

    // If tokens already stored and URL has no params (React StrictMode double-run), return existing
    const existingToken = localStorage.getItem('accessToken')
    const hasUrlParams = search && search.length > 1
    const hasHashParams = window.location.hash && window.location.hash.length > 1
    if (existingToken && !hasUrlParams && !hasHashParams) {
      const user = this.getUser()
      if (user) {
        return {
          accessToken: existingToken,
          refreshToken: localStorage.getItem('refreshToken') || '',
          expiresIn: parseInt(localStorage.getItem('token_expires_in') || '3600'),
          tokenType: 'Bearer',
          user: { id: user.uid, email: user.email || '', roles: ['user'] },
        }
      }
    }

    // Try hash fragment fallback
    if (!hasUrlParams && hasHashParams) {
      const hashParams = new URLSearchParams(window.location.hash.slice(1).replace(/^#/, ''))
      const token = hashParams.get('token') || params.get('token')
      const refreshToken = hashParams.get('refreshToken') || params.get('refreshToken')
      const expiresIn = hashParams.get('expiresIn') || params.get('expiresIn')
      const userId = hashParams.get('userId') || params.get('userId')
      const email = hashParams.get('email') || params.get('email')
      if (token && userId && email) {
        const response: LoginResponse = {
          accessToken: token,
          refreshToken: refreshToken || '',
          expiresIn: expiresIn ? parseInt(expiresIn, 10) : 3600,
          tokenType: 'Bearer',
          user: { id: userId, email: safeDecodeUri(email), roles: ['user'] },
        }
        this.storeTokens(response)
        window.history.replaceState({}, document.title, window.location.pathname)
        return response
      }
    }

    const token = params.get('token')
    const refreshToken = params.get('refreshToken')
    const expiresIn = params.get('expiresIn')
    const userId = params.get('userId')
    const email = params.get('email')
    if (!token || !userId || !email) throw new Error('Missing required authentication parameters')

    const response: LoginResponse = {
      accessToken: token,
      refreshToken: refreshToken || '',
      expiresIn: expiresIn ? parseInt(expiresIn, 10) : 3600,
      tokenType: 'Bearer',
      user: { id: userId, email: safeDecodeUri(email), roles: ['user'] },
    }
    this.storeTokens(response)
    window.history.replaceState({}, document.title, window.location.pathname)
    return response
  }

  private storeTokens(response: LoginResponse): void {
    localStorage.setItem('accessToken', response.accessToken)
    if (response.refreshToken) localStorage.setItem('refreshToken', response.refreshToken)
    localStorage.setItem('token_expires_in', response.expiresIn.toString())
    localStorage.setItem('token_type', response.tokenType)
    localStorage.setItem('token_timestamp', Date.now().toString())
    if (response.user) {
      localStorage.setItem('userId', response.user.id)
      localStorage.setItem('userEmail', response.user.email)
    }
    localStorage.setItem('projectKey', this.projectKey)
  }

  getAccessToken(): string | null {
    return localStorage.getItem('accessToken')
  }

  getUser(): User | null {
    const userId = localStorage.getItem('userId')
    const email = localStorage.getItem('userEmail')
    if (!userId || !email) return null
    return {
      uid: userId,
      email: safeDecodeUri(email),
      displayName: email.split('@')[0] || null,
      photoURL: null,
    }
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken()
    if (!token) return false
    const timestamp = localStorage.getItem('token_timestamp')
    const expiresIn = localStorage.getItem('token_expires_in')
    if (timestamp && expiresIn) {
      const elapsed = (Date.now() - parseInt(timestamp)) / 1000
      if (elapsed > parseInt(expiresIn)) {
        this.signOut()
        return false
      }
    }
    return true
  }

  signOut(): void {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('token_expires_in')
    localStorage.removeItem('token_type')
    localStorage.removeItem('token_timestamp')
    localStorage.removeItem('userId')
    localStorage.removeItem('userEmail')
    localStorage.removeItem('projectKey')
  }
}

function safeDecodeUri(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export const authClient = new AuthClient()
export default authClient
